import { Tooltip } from "@mui/material";
import cx from "classnames";
import WrapIf from "src/components/utils/WrapIf/WrapIf";
import type { RunStatus } from "src/queries/pipelines/pipelineRuns.types";
import { RUN_STATUSES } from "../../RunsStatusBars.constants";
import { AGGREGATED_STYLE_CLASSES, VARIANTS } from "./RunStatusBar.constants";
import { useRunStatusStyle } from "./RunStatusBar.hooks";
import type { RunStatusBarProps, VariantType } from "./RunStatusBar.types";
import { getRunStatusTooltipTitle } from "./RunStatusBar.utils";

const RunStatusBar = ({
  status = RUN_STATUSES.UNKNOWN,
  className,
  withTooltip,
  variant = "bar" as VariantType,
  isAggregated,
  ref,
  withoutAnimation,
  tooltipProps,
  ...props
}: RunStatusBarProps) => {
  let normalizedStatus: RunStatus =
    status && status.toUpperCase() in RUN_STATUSES
      ? RUN_STATUSES[status.toUpperCase() as RunStatus]
      : RUN_STATUSES.UNKNOWN;

  // "RunStatusBar" within StatusChip should not be treated as "failed with errors" status, but simply "failed" status
  if (normalizedStatus === RUN_STATUSES.FAILED_WITH_ERRORS) {
    normalizedStatus = RUN_STATUSES.FAILED;
  }

  const style = useRunStatusStyle(normalizedStatus, props.style);

  return (
    <WrapIf
      condition={!!withTooltip}
      With={Tooltip}
      title={getRunStatusTooltipTitle(status ?? RUN_STATUSES.UNKNOWN)}
      placement="left"
      disableInteractive
      arrow
      {...tooltipProps}
    >
      <div
        ref={ref}
        className={cx(
          VARIANTS[variant as VariantType],
          className,
          isAggregated && AGGREGATED_STYLE_CLASSES,
          "relative z-1",
          {
            "animate-pulse":
              normalizedStatus === RUN_STATUSES.RUNNING && !withoutAnimation,
          }
        )} // using 5.2px as width due to Chrome rendering bug
        {...props}
        style={style}
      ></div>
    </WrapIf>
  );
};

export default RunStatusBar;
